import classNames from "classnames";
import { DateTime } from "luxon";
import { FC, useContext, useEffect, useState } from "react";
import { DocumentGridStateType } from "../../../@types/documents";
import { IDatePeriod } from "../../../components/datePeriodPicker/datePeriodPicker";
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins";
import { DatePeriodPanel } from "../../../hoc/DatePeriodPanel/DatePeriodPanel";
import { ICashSessionStatusKizReportDTO, ICashSessionDTO, ICashSessionViewDTO, IChequeKizReportDTO, IChequeReport, IKizReport } from "../../../libs/coreapi-dto/accounting/cashSession";
import { CashSessionDataProvider } from "../../../Services/DataProviders/CashSessionDataProvider";
import useGridFilter from "../../../system/hooks/useGridFilter";
import { useDetailsTabsPanel } from "../../../system/hooks/useTabsPanel";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { usePluginContext } from "../../../system/providers/plugin";
import styles from "../../styles/index.module.scss";
import tabsStyles from "../../../pages/styles/homePage.module.scss";
import { TabsPanel } from "../../../components/tabs";
import { CashSessionPanel } from "./Components/CashSessionPanel";
import { Spinner } from "../../../components/spiner/Spinner";
import { DocumentStatusType, DocumentType } from "../../../@types/enumsGlobal";
import { KizButtonPanel } from "./Components/KizButtonPanel";
import style from './Components/KizButtonPanel.module.scss'
import { useTranslation } from "react-i18next";
import { MessageModalWindow } from "../../../components/modalWindows/MessageModalWindow";
import { ModalReportStatusKiz } from "./Components/ModalReportStatusKiz/ModalReportStatusKiz";
import { ModalCloseCashSession } from "./Components/ModalCloseCashSession/ModalCloseCashSession";

const DefaultGridFilter = (datePeriod) => {
    return {
        numberPerPage: 10,
        pageNumber: 1,
        columnFilters: [
            ...(datePeriod?.startDate
                ? [
                    {
                        name: "dateOpen",
                        value: datePeriod.startDate.toFormat("yyyyMMdd"),
                        operator: "MoreOrEq",
                        invisible: true
                    } as IGridColumnFilter,
                ]
                : []),
            ...(datePeriod?.endDate
                ? [
                    {
                        name: "dateOpen",
                        value: datePeriod.endDate.toFormat("yyyyMMdd"),
                        operator: "LessOrEq",
                        invisible: true
                    } as IGridColumnFilter,
                ]
                : []),
        ],
    };
};

interface IMessageModalProps {
    show: boolean;
    message: string;
    hideCancelButton?: boolean;
    onOk?: void
}

interface IReportItem {
    chequeNumber: string;
    idChequeGlobal: string;
    recipeNumber: string;
    kizReports: IKizReport[];
}
type ChequesKizStatus = 0 | 1 | 2 | 3 | 4 | 5 |null;

export interface IReportStatusKiz {
    show: boolean
    items: IChequeReport[] | null    
}

export const CashSessionGridView: FC<IGridProps> = (props) => {
    const { t } = useTranslation();
    const appContext = useAppContext();
    const cdp = new CashSessionDataProvider(appContext.coreApiService);
    const DefaultDatePeriod = { startDate: DateTime.now().plus({ days: -7 }), endDate: DateTime.now() } as IDatePeriod;
    const pluginContext = usePluginContext();

    const [gridFilter, dispatchGridFilter] = useGridFilter(DefaultGridFilter(DefaultDatePeriod));
    const [viewState, setViewState] = useState<DocumentGridStateType>("view");
    const [detailsTabsPanel, dispatchDetailsTabsPanel] = useDetailsTabsPanel(props.plugin.mnemocode);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);

    const [datePeriod, setDatePeriod] = useState<IDatePeriod>(DefaultDatePeriod);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [data, setData] = useState<ICashSessionViewDTO[]>([]);
    const [dataItem, setDataItem] = useState<ICashSessionDTO>() 
    const [sessionData, setSessionData] = useState<ICashSessionDTO>()
    
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>(props.data ?? []);
    const [selectedDocumentState, setSelectedDocumentState] = useState<string>('');

    const isSessionClosed = data.find((x) => x.idGlobal === selectedItem?.idGlobal)?.documentState === "Closed"

    //кассовые чеки
    const [reports, setReports] = useState<IChequeReport[]>([])
    const [idReports, setIdReports] = useState<string>('')
    const [showMessageModal, setShowMessageModal] = useState<IMessageModalProps>({
        show: false,
        message: "",
        hideCancelButton: false,
    });
    const [showModalCloseSession, setShowModalCloseSession] = useState<boolean>(false);
    const [showModalSchemaReport, setShowModalSchemaReport] = useState<IMessageModalProps>({ show: false, message: "" });
    const [dataSchemeToReport, setDataSchemeToReport] = useState<IChequeKizReportDTO | null>(null);
    const [reportStatusKiz, setReportStatusKiz] = useState<IReportStatusKiz>({
        show: true,
        items: null
    })
    const kizCell = selectedItem?.cells?.find(c => c.propertyName === 'chequesKizStatus');
    const kizStatus: ChequesKizStatus = kizCell?.value !== undefined
        ? kizCell.value as ChequesKizStatus
        : null;

    useEffect(() => {

        if (selectedItem) {
            const documentState = selectedItem.cells.find((c) => c.propertyName === "documentState")?.value as DocumentStatusType;
            setSelectedDocumentState(documentState);
            cdp.getById(selectedItem.idGlobal, (dataItem: ICashSessionDTO) => {
                setDataItem(dataItem);
            })            
        } else {
            setDataItem(undefined);
        }
    }, [selectedItem]);
   
        //Запрос статусов КИЗ
    const handleRequestStatusKiz = () => {
        if (!selectedItem || selectedDocumentState === 'Open') return;
        
        cdp.getChequesKizStatus(selectedItem.idGlobal, (e) => {
            
            if (e?.message.statusCode === 500) {
                if (e?.message.title?.includes(`Ключ id_document_global ${selectedItem.idGlobal} уже существует`)) {                    
                    setShowMessageModal({
                        show: true,
                        message: t('documentsCommandsPanel.requestStatusDuplicateError'),
                        hideCancelButton: true,
                    });
                } else if (e?.message.title === "Отсутствуют КИЗ для обработки") {                    
                    setShowMessageModal({
                        show: true,
                        message: t('documentsCommandsPanel.requestStatusNoKizMessage'),
                        hideCancelButton: true,
                    });
                }
            } else {                
                setShowMessageModal({
                    show: true,
                    message: t('documentsCommandsPanel.requestStatusKizMessage'),
                    hideCancelButton: true,                
                });
            }
        });
    };
    
        // 2 запрос отчета по статусам       
    const handleReportStatusKiz = async () => {
        if (!selectedItem || !dataItem) {
            console.error("No item selected or data not available");
            return;
        }

        try {
            const reportItem: ICashSessionStatusKizReportDTO = {
                idGlobal: dataItem.idGlobal,
                idCashRegister: dataItem.idCashRegister,
                dateOpen: dataItem.dateOpen,
                dateClose: dataItem.dateClose,
                idGlobalUserDataOpen: dataItem.idUserDataOpen,
                idGlobalUserDataClose: dataItem.idUserDataClose,
                documentState: dataItem.documentState,
                quantity: dataItem.quantity,
                sum: dataItem.sum,
                sumCash: dataItem.sumCash,
                sumCredit: dataItem.sumCredit,
                quantityReturns: dataItem.quantityReturns,
                sumReturns: dataItem.sumReturns,
                sumCashReturns: dataItem.sumCashReturns,
                sumCreditReturns: dataItem.sumCreditReturns,
                sumDiscount: dataItem.sumDiscount,
            };
            setIsSubmitting(true);
            cdp.getChequesKizReport(reportItem, (idGlobal: string, reports: IChequeReport[]) => {
                if (idGlobal === selectedItem.idGlobal) {                    
                    setReports(reports);
                    setIdReports(idGlobal);
                    setReportStatusKiz({
                        show: true,
                        items: reports
                    });

                }
            });
            setIsSubmitting(false);
        } catch (error) {
            console.error("Ошибка при получении отчета:", error);
            setIsSubmitting(false);
            setShowMessageModal({
                show: true,
                message: t('errors.requestExecutionError'),
                hideCancelButton: false
            });
        }
    };

        // 3 отправка 511/512 схемы
    const handleSend511_512 = () => {
        if (!selectedItem || !dataItem) {
            console.error("No item selected or data not available");
            return;
        }

        try {
            const reportItem: ICashSessionStatusKizReportDTO = {
                idGlobal: dataItem.idGlobal,
                idCashRegister: dataItem.idCashRegister,
                dateOpen: dataItem.dateOpen,
                dateClose: dataItem.dateClose,
                idGlobalUserDataOpen: dataItem.idUserDataOpen,
                idGlobalUserDataClose: dataItem.idUserDataClose,
                documentState: dataItem.documentState,
                quantity: dataItem.quantity,
                sum: dataItem.sum,
                sumCash: dataItem.sumCash,
                sumCredit: dataItem.sumCredit,
                quantityReturns: dataItem.quantityReturns,
                sumReturns: dataItem.sumReturns,
                sumCashReturns: dataItem.sumCashReturns,
                sumCreditReturns: dataItem.sumCreditReturns,
                sumDiscount: dataItem.sumDiscount,
            };
            setIsSubmitting(true);
            cdp.getChequesKizReport(reportItem, (idGlobal: string, responseData: IChequeReport[]) => {               
                try {
                    if (idGlobal === selectedItem.idGlobal) {
                        
                        const dataArray = Array.isArray(responseData)
                            ? responseData
                            : [responseData];

                        const processedData = dataArray.map(report => ({
                            ...report,
                            recipeNumber: report.recipeNumber ?? "",
                            kizReports: report.kizReports
                                ?.filter(kiz => kiz.kizStateIS === "Proc") // Фильтруем только КИЗы с kizStateIS = "Proc"                            
                                ?.map(kiz => ({
                                    ...kiz,
                                    isSending: true,
                                })) ?? [],
                        }));

                        const filteredData = processedData.filter(report => report.kizReports.length > 0);

                        const chequeNumbers = filteredData.map(report => report.chequeNumber);    
                        
                        setShowModalSchemaReport({
                            show: true,
                            message: chequeNumbers.length
                                ? `Сейчас будут отправлены схемы 511 и/или 521 по следующим чекам: ${chequeNumbers.join(', ')}. Продолжить?`
                                : 'Нет данных для отправки',
                            hideCancelButton: false,
                        });

                        setReports(filteredData);
                        setIdReports(idGlobal);
                        setDataSchemeToReport({
                            idGlobal: idGlobal,
                            reports: filteredData
                        })
                    }
                } finally {
                    setIsSubmitting(false);
                }
            });

        } catch (error) {
            console.error("Ошибка при получении отчета:", error);
            setIsSubmitting(false);
            setShowMessageModal({
                show: true,
                message: t('errors.requestExecutionError'),
                hideCancelButton: false
            });
        }
    };
    //кассовые чеки
       
    useEffect(() => {
        setIsSubmitting(true)
        cdp.getCashSession(gridFilter, async (entities, totalCount) => {
            setTotalCount(totalCount);
            setData(entities);
            setIsSubmitting(false)
            setViewState("view");
        });
    }, [viewState, gridFilter]);

    const handleOpenCloseCashSessionModal = () => {
        if (!selectedItem?.idGlobal) return;

        setSessionData(undefined);
        setShowModalCloseSession (true)
        
        cdp.getById(selectedItem.idGlobal, (data) => {
            setSessionData(data)
        }, (error) => {
            console.error(error);
            setShowMessageModal({
                show: true,
                message: t('errors.loadSessionDataError'),
                hideCancelButton: true,
            });
            setShowModalCloseSession(false);
        })
    }

    const handleCloseCashSession = (dateClose: DateTime | undefined | string) => {
        if (!selectedItem || !sessionData) return;

        const entity = {
            idGlobal: selectedItem.idGlobal,
            idCashRegister: sessionData.idCashRegister,
            idUserDataClose: sessionData.idGlobalUserDataOpen,
            dateClose: dateClose,
            documentState: 'Closed'
        } as ICashSessionDTO
    
        setIsSubmitting(true);
        
        cdp.updateSession(selectedItem.idGlobal, entity, () => {
            setIsSubmitting(false);
            setViewState("refresh");
            setSelectedItem(undefined);
            setSessionData(undefined);
            setShowModalCloseSession(false);
        });
    }

    return (
        <>
            <PluginWrapper>
                <ToolbarWrapper className={style.toolbarWrapper}>
                    <CashSessionPanel
                        closeSession={{
                            onClick: () => {
                                if (selectedDocumentState !== "Closed") {
                                    cdp.closeSession(selectedItem?.idGlobal as string, data.find((c) => c.idGlobal === selectedItem?.idGlobal) as ICashSessionViewDTO, () => {
                                        setViewState("refresh");
                                        setSelectedItem(undefined);
                                    });
                                }
                            },
                            disabled: selectedItem ? false : true,
                        }}
                        refresh={{
                            onClick: () => {
                                setViewState("refresh");
                                setSelectedItem(undefined);
                            },
                        }}
                        permission={props.plugin.permission}
                        advancedFeatures
                        items={[
                            {
                                label: t('cashSession.closeCashSession'),
                                onClick: () => handleOpenCloseCashSessionModal(),
                                disabled: isSessionClosed || !selectedItem
                            }
                        ]}
                    />
                    <DatePeriodPanel
                        datePeriod={datePeriod}
                        onDatePeriodChange={(date: IDatePeriod) => {
                            setDatePeriod(date);
                            dispatchGridFilter({
                                type: "sortByFilters",
                                payload: {
                                    gridColumnFilter: [
                                        ...(date?.startDate
                                            ? [
                                                {
                                                    name: "dateOpen",
                                                    value: date.startDate.toFormat("yyyyMMdd"),
                                                    operator: "MoreOrEq",
                                                } as IGridColumnFilter,
                                            ]
                                            : []),
                                        ...(date?.endDate
                                            ? [
                                                {
                                                    name: "dateOpen",
                                                    value: date.endDate.toFormat("yyyyMMdd"),
                                                    operator: "LessOrEq",
                                                } as IGridColumnFilter,
                                            ]
                                            : []),
                                    ],
                                },
                            });
                        }}
                    />
                    <KizButtonPanel
                        kizStatus = {kizStatus}
                        selectedItem={selectedItem ?? {} as IGridRow}
                        onRequestStatusKiz={handleRequestStatusKiz}
                        onReportStatusKiz={handleReportStatusKiz}
                        onSend511_512={handleSend511_512}                        
                    />
                </ToolbarWrapper>

                <div className={styles.gridWrapper}>
                    <div className={styles.masterGrid}>
                        {isSubmitting ? (
                            <Spinner />
                        ) :
                            <DefaultGrid     
                                autoSelect
                                showQuantity                           
                                сhequesKizStatus
                                gridId={props.gridId}
                                data={data}
                                filter={gridFilter}
                                totalCount={totalCount}                                
                                searching
                                separator
                                dataProvider={cdp}
                                documentStatus                                
                                plugin={props.plugin}
                                getView={(gridFilter,callback) => {
                                    cdp.getCashSession(gridFilter, (entities) => {
                                        callback(entities)
                                        setSelectedItem(undefined);
                                    })
                                }}
                                selectedItem={selectedItem}
                                selectedItems={selectedItems}
                                multipleSelect={props.multipleSelect}
                                onMultipleSelect={(rows) => {
                                    setSelectedItems(rows ? rows : []);
                                    props?.onMultipleSelect?.(rows ? rows : []);
                                }}
                                onSelect={(row) => {
                                    setSelectedItem(row);
                                    row ? props?.onSelect?.(row, data.find(x => x.idGlobal === row?.idGlobal)) : props?.onSelect?.(undefined);
                                    row ? pluginContext.masterGrid.onSelectDetailEvent(row) : pluginContext.masterGrid.onUnselectDetailEvent();
                                    row ? pluginContext.masterGrid.onSelectEvent(row, DocumentType.cashSession) : pluginContext.masterGrid.onUnselectEvent();
                                }}
                                onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                                onFilterDelete={(i) => {
                                    i.propertyName === "documentDate" && setDatePeriod({ startDate: null, endDate: null });
                                    dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName });
                                }}
                                onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                                onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                            />}
                    </div>
                    {props.isDetail &&
                        <>
                            <div className={styles.detailsTabView}>
                                {selectedItem && detailsTabsPanel.tabs.length > 0 &&
                                    <>
                                        <div className={tabsStyles.tabsPanelWrapper}>
                                            <div id="detailsTabsPanelWrapper" style={{ position: "relative", overflow: "hidden", width: "100%" }}>
                                                <TabsPanel
                                                    id="detailsTabsPanel"
                                                    activeId={detailsTabsPanel.currentTab?.id}
                                                    tabs={detailsTabsPanel.tabs}
                                                    onActive={(id) => {
                                                        dispatchDetailsTabsPanel({ type: "activate", payload: id });
                                                    }}
                                                ></TabsPanel>
                                            </div>
                                        </div>
                                        <div className={tabsStyles.contentWrapper}>
                                            {detailsTabsPanel.tabs.map((item) => {
                                                return (
                                                    <div key={item.id} className={classNames(item.id === detailsTabsPanel.currentTab?.id ? tabsStyles.contentVisible : tabsStyles.contentHidden)}>
                                                        {item.view.content}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                }
                            </div>
                        </>
                    }
                </div>
            </PluginWrapper>

            {showMessageModal.show && (
                <MessageModalWindow
                    ok={{
                        onClick: () => {
                            setShowMessageModal({ show: false, message: "" });
                            setViewState('refresh')
                        },
                    }}
                    cancel={{
                        onClick: () => setShowMessageModal({ show: false, message: "" }),
                        hidden: showMessageModal.hideCancelButton,
                    }}
                    message={showMessageModal.message}
                />
            )}
            {showModalSchemaReport.show && (
                <MessageModalWindow
                ok={{
                    onClick: () => {
                        setShowModalSchemaReport({ show: false, message: "" });
                        if (dataSchemeToReport) {
                            cdp.getSchemeToReport(dataSchemeToReport, e => console.log(e));
                        };
                        setViewState('refresh')
                    }
                }}
                    cancel={{
                        onClick: () => setShowModalSchemaReport({ show: false, message: "" }),                        
                    }}
                    message={showModalSchemaReport.message}
                />
            )}
            {reportStatusKiz.show && reportStatusKiz.items && reports && (
                <ModalReportStatusKiz
                    idReports={idReports}
                    reports={reports}
                    cancel={() => setReportStatusKiz({ show: false, items: null})}
                    ok={() => setViewState('refresh')}
                />
            )}
            {showModalCloseSession && (
                <ModalCloseCashSession
                    ok={(combinedDateTime) => {
                        handleCloseCashSession(combinedDateTime)
                    }}
                    cancel={() => {
                        setShowModalCloseSession(false);
                        setSessionData(undefined);
                    }}
                />
            )}            
        </>
    );
};